/**
 * @file MainScrollContext.tsx
 * @brief Access the admin shell main scroller + chrome expand for anchor jumps.
 *
 * @author DES
 * @date 06-07-2026
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import {
  getMainScrollRoot,
  scrollMainToSection,
  scrollMainToTop,
} from "@/shared/scroll/scrollMainTo";
import { MainScrollContext } from "./main-scroll-context";

interface MainScrollProviderProps {
  expandChrome: () => void;
  /** Freeze scroll-collapse while an anchor jump animates (prevents jitter). */
  setProgrammaticScroll?: (active: boolean) => void;
  chromeCollapsed?: boolean;
  headerHeight?: number;
  /** See MainScrollContextValue.setFlushBottom. */
  setFlushBottom?: (flush: boolean) => void;
  children: ReactNode;
}

// Fallback when `scrollend` is unavailable or never fires.
const SCROLL_SETTLE_MS = 900;

export function MainScrollProvider({
  expandChrome,
  setProgrammaticScroll,
  chromeCollapsed = false,
  headerHeight = 56,
  setFlushBottom,
  children,
}: MainScrollProviderProps) {
  const flushBottom = useCallback(
    (flush: boolean) => setFlushBottom?.(flush),
    [setFlushBottom],
  );
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSectionRef = useRef<string | null>(null);
  const programmaticRef = useRef(false);

  const releaseProgrammaticScroll = useCallback(() => {
    programmaticRef.current = false;
    pendingSectionRef.current = null;
    setProgrammaticScroll?.(false);
    if (releaseTimer.current) {
      clearTimeout(releaseTimer.current);
      releaseTimer.current = null;
    }
  }, [setProgrammaticScroll]);

  const holdChrome = useCallback(() => {
    programmaticRef.current = true;
    setProgrammaticScroll?.(true);

    if (releaseTimer.current) clearTimeout(releaseTimer.current);

    const main = getMainScrollRoot();
    if (main && "onscrollend" in main) {
      const onScrollEnd = () => {
        main.removeEventListener("scrollend", onScrollEnd);
        releaseProgrammaticScroll();
      };
      main.addEventListener("scrollend", onScrollEnd, { once: true });
    }

    releaseTimer.current = setTimeout(() => {
      releaseProgrammaticScroll();
    }, SCROLL_SETTLE_MS);
  }, [releaseProgrammaticScroll, setProgrammaticScroll]);

  useEffect(
    () => () => {
      releaseProgrammaticScroll();
    },
    [releaseProgrammaticScroll],
  );

  const runScroll = useCallback((scroll: () => boolean) => {
    expandChrome();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scroll();
      });
    });
  }, [expandChrome]);

  const scrollToSection = useCallback(
    (sectionId: string) => {
      if (pendingSectionRef.current === sectionId && programmaticRef.current) {
        return true;
      }

      pendingSectionRef.current = sectionId;
      holdChrome();

      runScroll(() => {
        const scrolled = scrollMainToSection(sectionId);
        if (!scrolled) {
          // Target not mounted yet (skeleton → cards). One quiet retry.
          window.setTimeout(() => {
            if (pendingSectionRef.current !== sectionId) return;
            scrollMainToSection(sectionId, { behavior: "auto" });
          }, 200);
        }
        return scrolled;
      });

      return true;
    },
    [holdChrome, runScroll],
  );

  const scrollToTop = useCallback(() => {
    pendingSectionRef.current = "__top__";
    holdChrome();
    runScroll(() => scrollMainToTop());
    return true;
  }, [holdChrome, runScroll]);

  const isProgrammaticScrolling = useCallback(
    () => programmaticRef.current,
    [],
  );

  const value = useMemo(
    () => ({
      scrollToSection,
      scrollToTop,
      isProgrammaticScrolling,
      pauseChromeCollapse: holdChrome,
      chromeCollapsed,
      headerHeight,
      setFlushBottom: flushBottom,
    }),
    [
      scrollToSection,
      scrollToTop,
      isProgrammaticScrolling,
      holdChrome,
      chromeCollapsed,
      headerHeight,
      flushBottom,
    ],
  );

  return (
    <MainScrollContext.Provider value={value}>
      {children}
    </MainScrollContext.Provider>
  );
}
